import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2 } from 'lucide-react';
import {
  GIORNI_SETTIMANA,
  PROFILI_RAPIDI,
  SETPOINT_PROFILO,
  arrotondaSetpoint,
  fasceValide,
  fasciaAttiva,
  minutiDaOrario,
  type FasciaOraria,
  type GiornoSettimana,
  type ProfiloRapido,
  type ProgrammaOrario,
} from '@emacomfort/domain';
import { useComando, useDevice, useObsoleto, useStato } from '@/app/DeviceProvider';
import { useFormato } from '@/app/formato';
import { useAvvisi } from '@/app/useAvvisi';
import { Avviso, Bottone, Campo, Card, Conferma, Interruttore, Pagina, Select, StatoComandoVista, Stepper, cx, stili } from '@/ui';
import { IconaZonaVista } from '@/ui/icone';
import { useZone } from '../comune';
import s from '../features.module.css';

const ORARI = Array.from({ length: 48 }, (_, i) => `${String(Math.floor(i / 2)).padStart(2, '0')}:${i % 2 ? '30' : '00'}`);

export function ProgrammazionePage() {
  const { t } = useTranslation();
  const f = useFormato();
  const { client, invia } = useDevice();
  const zone = useZone();
  const { obsoleto } = useObsoleto();
  const adesso = useStato((st) => st.adesso);
  const mostra = useAvvisi((a) => a.mostra);
  const giorni = t('giorni', { returnObjects: true }) as unknown as string[];
  const giorniBrevi = t('giorniBrevi', { returnObjects: true }) as unknown as string[];

  const [zonaId, setZonaId] = useState<string | null>(null);
  const zona = zone.find((z) => z.id === zonaId) ?? zone[0];
  const [giorno, setGiorno] = useState<GiornoSettimana>(((new Date().getDay() + 6) % 7) as GiornoSettimana);
  const [programma, setProgramma] = useState<ProgrammaOrario | null>(null);
  const [sporco, setSporco] = useState(false);
  const [profilo, setProfilo] = useState<ProfiloRapido | null>(null);
  const [daRimuovere, setDaRimuovere] = useState<number | null>(null);
  const comandoProgramma = useComando(zona ? `programma:${zona.id}` : 'programma:nessuna');
  const comandoProfilo = useComando('profilo');

  useEffect(() => {
    if (!zona) return;
    let vivo = true;
    void client.getProgramma(zona.id).then((p) => {
      if (vivo) {
        setProgramma(p);
        setSporco(false);
      }
    });
    return () => {
      vivo = false;
    };
  }, [client, zona]);

  const fasce = programma?.giorni[giorno] ?? [];
  const valide = useMemo(() => fasceValide(fasce), [fasce]);
  const inCorso = programma ? fasciaAttiva(programma, new Date(adesso)) : undefined;

  const aggiorna = (nuoveFasce: FasciaOraria[]) => {
    if (!programma) return;
    setProgramma({ ...programma, giorni: { ...programma.giorni, [giorno]: nuoveFasce.sort((a, b) => minutiDaOrario(a.da) - minutiDaOrario(b.da)) } });
    setSporco(true);
  };

  const salva = async () => {
    if (!programma || !zona) return;
    const r = await invia({ azione: 'set_programma', zonaId: zona.id, programma });
    if (r.esito === 'applicato') {
      setSporco(false);
      mostra(t('programmazione.salvato'), 'ok');
    }
  };

  return (
    <Pagina titolo={t('programmazione.titolo')}>
      <Card titolo={t('programmazione.profili')}>
        <div className={s.chips}>
          {PROFILI_RAPIDI.map((p) => (
            <button key={p} type="button" className={s.chip} onClick={() => setProfilo(p)} disabled={obsoleto}>
              {t(`programmazione.profilo.${p}`)} · {f.temp(SETPOINT_PROFILO[p], 0)}
            </button>
          ))}
        </div>
        <StatoComandoVista esito={comandoProfilo?.esito} motivo={comandoProfilo?.motivo} />
      </Card>

      <div className={s.colonne}>
        <Card titolo={t('programmazione.zona')}>
          <div className={s.chips}>
            {zone.map((z) => (
              <button key={z.id} type="button" className={cx(s.chip, z.id === zona?.id && s.chipAttivo)} aria-pressed={z.id === zona?.id} onClick={() => setZonaId(z.id)}>
                <IconaZonaVista icona={z.icona} size={22} aria-hidden />
                {z.nome}
              </button>
            ))}
          </div>
          {programma && (
            <>
              <div className={s.impostazione}>
                <span>{programma.attivo ? t('programmazione.attivo') : t('programmazione.disattivo')}</span>
                <Interruttore
                  acceso={programma.attivo}
                  onChange={(v) => {
                    setProgramma({ ...programma, attivo: v });
                    setSporco(true);
                  }}
                  etichetta={t('programmazione.attivo')}
                  testoAcceso={t('comune.attivo')}
                  testoSpento={t('comune.spento')}
                />
              </div>
              <p className={s.letturaNota}>{t('programmazione.attivoNota')}</p>
            </>
          )}
        </Card>

        <Card titolo={t('programmazione.fasce')}>
          <div className={s.giorni} role="group" aria-label={t('programmazione.fasce')}>
            {GIORNI_SETTIMANA.map((g) => (
              <button key={g} type="button" className={cx(s.chip, g === giorno && s.chipAttivo)} aria-pressed={g === giorno} aria-label={giorni[g]} onClick={() => setGiorno(g)}>
                {giorniBrevi[g]}
              </button>
            ))}
          </div>
          {fasce.length === 0 && <p className={stili.muto}>{t('programmazione.nessunaFascia')}</p>}
          {fasce.map((fa, i) => {
            const corrente = inCorso === fa;
            return (
              <div key={`${fa.da}-${i}`} className={cx(s.fascia, corrente && s.fasciaInCorso)}>
                <Campo etichetta={t('programmazione.da')}>
                  <Select value={fa.da} onChange={(e) => aggiorna(fasce.map((x, j) => (j === i ? { ...x, da: e.target.value } : x)))}>
                    {ORARI.map((o) => (
                      <option key={o} value={o}>
                        {o}
                      </option>
                    ))}
                  </Select>
                </Campo>
                <Campo etichetta={t('programmazione.a')}>
                  <Select value={fa.a} onChange={(e) => aggiorna(fasce.map((x, j) => (j === i ? { ...x, a: e.target.value } : x)))}>
                    {[...ORARI.slice(1), '24:00'].map((o) => (
                      <option key={o} value={o}>
                        {o}
                      </option>
                    ))}
                  </Select>
                </Campo>
                <div className={stili.campo}>
                  <span className={stili.etichetta}>
                    {t('programmazione.setpoint')} {corrente && `· ${t('programmazione.inCorso')}`}
                  </span>
                  <Stepper
                    dimensione="terziario"
                    valore={f.tempNumero(fa.setpoint)}
                    onMeno={() => aggiorna(fasce.map((x, j) => (j === i ? { ...x, setpoint: arrotondaSetpoint(Math.max(zona?.limiti.min ?? 5, x.setpoint - 0.5)) } : x)))}
                    onPiu={() => aggiorna(fasce.map((x, j) => (j === i ? { ...x, setpoint: arrotondaSetpoint(Math.min(zona?.limiti.max ?? 35, x.setpoint + 0.5)) } : x)))}
                    etichettaMeno={t('zone.meno', { zona: `${fa.da}–${fa.a}` })}
                    etichettaPiu={t('zone.piu', { zona: `${fa.da}–${fa.a}` })}
                  />
                </div>
                <Bottone variante="pericolo" quadrato aria-label={`${t('programmazione.rimuovi')} ${fa.da}–${fa.a}`} onClick={() => setDaRimuovere(i)}>
                  <Trash2 size={24} aria-hidden />
                </Bottone>
              </div>
            );
          })}
          {!valide && <Avviso tono="err">{t('programmazione.fasceNonValide')}</Avviso>}
          <div className={stili.riga}>
            <Bottone onClick={() => aggiorna([...fasce, { da: '18:00', a: '22:00', setpoint: zona?.setpoint ?? 20 }])} disabled={!programma}>
              <Plus size={22} aria-hidden />
              {t('programmazione.aggiungi')}
            </Bottone>
            <Bottone
              onClick={() => {
                if (!programma) return;
                const feriali: ProgrammaOrario['giorni'] = { ...programma.giorni };
                ([0, 1, 2, 3, 4] as GiornoSettimana[]).forEach((g) => {
                  feriali[g] = fasce.map((x) => ({ ...x }));
                });
                setProgramma({ ...programma, giorni: feriali });
                setSporco(true);
              }}
              disabled={!programma}
            >
              {t('programmazione.copiaSuFeriali')}
            </Bottone>
            <span className={stili.spazio} />
            <Bottone variante="primario" onClick={() => void salva()} disabled={!sporco || !valide || obsoleto}>
              {t('programmazione.salva')}
            </Bottone>
          </div>
          <StatoComandoVista esito={comandoProgramma?.esito} motivo={comandoProgramma?.motivo} />
        </Card>
      </div>

      <Conferma
        aperta={profilo !== null}
        titolo={profilo ? t('programmazione.applicaProfilo', { profilo: t(`programmazione.profilo.${profilo}`), temp: f.temp(SETPOINT_PROFILO[profilo], 0) }) : ''}
        onAnnulla={() => setProfilo(null)}
        onConferma={() => {
          if (profilo) void invia({ azione: 'applica_profilo', profilo });
          setProfilo(null);
        }}
      >
        {t('programmazione.applicaProfiloNota')}
      </Conferma>
      <Conferma
        aperta={daRimuovere !== null}
        titolo={daRimuovere !== null && fasce[daRimuovere] ? t('programmazione.confermaRimozione', { da: fasce[daRimuovere]!.da, a: fasce[daRimuovere]!.a }) : ''}
        pericolosa
        testoConferma={t('programmazione.rimuovi')}
        onAnnulla={() => setDaRimuovere(null)}
        onConferma={() => {
          if (daRimuovere !== null) aggiorna(fasce.filter((_, j) => j !== daRimuovere));
          setDaRimuovere(null);
        }}
      />
    </Pagina>
  );
}
